import { useCallback } from 'react';
import HoverDetectionContainer from '../components/v2BaseComponents/HoverDetectionContainer';
import { useToggle } from './useToggle';
import { useIsDisabledByParent } from '../components/v2BaseComponents/IsDisabledProvider';
import { isPresent } from 'ts-is-present';
import { StyleProp, ViewStyle } from 'react-native';

export type State = 'default' | 'hovered' | 'pressed' | 'inactive';

export type StateStyles<TStyles> = {
    default: TStyles;
    hovered?: TStyles;
    pressed?: TStyles;
    inactive?: TStyles;
};

export type UseInteractionStateStylesArgs<TStyles> = {
    isDisabled?: boolean;
    stateStyles: StateStyles<TStyles>;
    hoverDetectionContainerStyles?: StyleProp<ViewStyle>;
};

export type UseInteractionStateStylesReturnValue<TStyles> = {
    styles: TStyles;
    onPressIn: () => void;
    onPressOut: () => void;
    renderInHoverContainer: (children: React.ReactNode, style?: StyleProp<ViewStyle>) => React.ReactNode;
    state: State;
};

export const useInteractionStateStyles = <TStyles,>({
    isDisabled = false,
    stateStyles,
    hoverDetectionContainerStyles,
}: UseInteractionStateStylesArgs<TStyles>): UseInteractionStateStylesReturnValue<TStyles> => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const isHovered = useToggle(false);
    const onHoverChange = useCallback(
        (hovered: boolean) => {
            hovered ? isHovered.toggleOn() : isHovered.toggleOff();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isHovered.toggleOff, isHovered.toggleOn],
    );
    const isPressed = useToggle(false);

    // sorted by priority
    const state: State = [
        isDisabledByParent ? 'inactive' : null,
        isPressed.value ? 'pressed' : null,
        isHovered.value ? 'hovered' : null,
        'default',
    ]
        .filter(isPresent)
        .find(Boolean) as State;

    const style = stateStyles[state] || stateStyles.default;

    return {
        styles: style,
        state,
        onPressIn: isPressed.toggleOn,
        onPressOut: isPressed.toggleOff,
        renderInHoverContainer: (children: React.ReactNode, style?: StyleProp<ViewStyle>) => (
            <HoverDetectionContainer style={[style, hoverDetectionContainerStyles]} onHoverChange={onHoverChange}>
                {children}
            </HoverDetectionContainer>
        ),
    };
};
